import React from "react";
import {Routes, Route, Navigate} from "react-router-dom";
import {AuthProvider} from "./contexts/AuthContext";
import MainLayout from "./layouts/MainLayout";
import LoginPage from "./pages/auth/LoginPage";
import DashboardPage from "./pages/dashboard/DashboardPage";
import FarmListPage from "./pages/farm/FarmListPage";
import PondListPage from "./pages/pond/PondListPage";
import UserListPage from "./pages/users/UserListPage";
import ConfigPage from "./pages/config/ConfigPage";
import TenantListPage from "./pages/tenant/TenantListPage";
import HelpPage from "./pages/help/HelpPage";
import PredictiveAnalyticsPage from "./pages/predictive/PredictiveAnalyticsPage";
import AdvancedPredictivePage from "./pages/predictive/AdvancedPredictivePage";
import PrivateRoute from "./routes/PrivateRoute";
import {UserRole} from "./api/user";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import PondDetailPage from "./pages/pond/PondDetailPage";
import TemplateManagementPage from "./pages/feed/TemplateManagementPage";
import OnboardingPage from "./pages/auth/OnboardingPage";
import RequestAccessPage from "./pages/auth/RequestAccessPage";
import AccessRequestsPage from "./pages/admin/AccessRequestsPage";
import MarketPricePage from "./pages/market/MarketPricePage";
import PublicDashboardPage from "./pages/dashboard/PublicDashboardPage";

import {GoogleOAuthProvider} from "@react-oauth/google";

const App: React.FC = () => {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || "YOUR_GOOGLE_CLIENT_ID"}>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<PublicDashboardPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/request-access" element={<RequestAccessPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route element={<PrivateRoute />}>
            <Route path="/" element={<MainLayout />}>
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="farms" element={<FarmListPage />} />
              <Route path="ponds" element={<PondListPage />} />
              <Route path="ponds/:id" element={<PondDetailPage />} />
              <Route path="template-management" element={<TemplateManagementPage />} />

              <Route path="predictive" element={<PredictiveAnalyticsPage />} />
              <Route path="advanced-predictive" element={<AdvancedPredictivePage />} />
              <Route path="market-prices" element={<MarketPricePage />} />
              <Route path="help" element={<HelpPage />} />
              <Route
                path="tenants"
                element={
                  <PrivateRoute roles={[UserRole.SUPER_ADMIN]}>
                    <TenantListPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="access-requests"
                element={
                  <PrivateRoute roles={[UserRole.SUPER_ADMIN]}>
                    <AccessRequestsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="users"
                element={
                  <PrivateRoute roles={[UserRole.SUPER_ADMIN, UserRole.TENANT_ADMIN, UserRole.FARMER_ADMIN]}>
                    <UserListPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="config"
                element={
                  <PrivateRoute roles={[UserRole.SUPER_ADMIN, UserRole.TENANT_ADMIN, UserRole.FARMER_ADMIN]}>
                    <ConfigPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="template-management"
                element={
                  <PrivateRoute roles={[UserRole.SUPER_ADMIN]}>
                    <TemplateManagementPage />
                  </PrivateRoute>
                }
              />
              {/* Keep old routes for backward compatibility */}
              <Route path="farm" element={<Navigate to="/farms" replace />} />
              <Route path="pond" element={<Navigate to="/ponds" replace />} />
              <Route path="feed" element={<Navigate to="/feeds" replace />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
};

export default App;
